// src/components/invoices/InvoiceBuilderShadcn.jsx - Pure shadcn implementation
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Send, Save, Eye } from 'lucide-react';

// Import shadcn components
import { Button } from "../ui/button";
import { Alert, AlertDescription } from "../ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";

// Import our form component
import InvoiceFormShadcn from './forms/InvoiceFormShadcn';
import PageContainer from '../ui/layout/PageContainer/PageContainer';

// Import hooks
import { useInvoices } from '../../hooks/useInvoices';
import { useCustomers } from '../../hooks/useCustomers';
import { useToast } from "../../hooks/use-toast";

const InvoiceBuilderShadcn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { invoiceId } = useParams();
  const { toast } = useToast();

  // Determine if we're editing
  const isEditing = !!invoiceId;
  const initialCustomerId = location.state?.customerId;

  // Get data from hooks
  const {
    selectedInvoice,
    loading: invoicesLoading,
    error: invoicesError,
    createInvoice,
    updateInvoice,
    loadInvoiceDetails,
    sendInvoice,
    clearInvoicesError,
    clearSelectedInvoice,
    isConnected
  } = useInvoices();

  const {
    customers,
    loading: customersLoading,
    error: customersError,
    loadCustomers
  } = useCustomers();

  // Local state
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  // Load customers on mount
  useEffect(() => {
    if (isConnected && customers.length === 0 && !customersLoading) {
      loadCustomers();
    }
  }, [isConnected, customers.length, customersLoading, loadCustomers]);

  // Load invoice details if editing
  useEffect(() => {
    if (isEditing && invoiceId && !selectedInvoice && isConnected) {
      loadInvoiceDetails(invoiceId);
    }
  }, [isEditing, invoiceId, selectedInvoice, isConnected, loadInvoiceDetails]);

  // Clear selected invoice on unmount
  useEffect(() => {
    return () => {
      if (isEditing) {
        clearSelectedInvoice();
      }
    };
  }, [isEditing, clearSelectedInvoice]);

  // Navigation handlers
  const handleBack = () => {
    navigate('/invoices');
  };

  // Form submission handler
  const handleSubmit = async (formData) => {
    try {
      setSaving(true);
      clearInvoicesError();

      if (isEditing) {
        await updateInvoice(invoiceId, formData);
        toast({
          title: "Invoice Updated",
          description: "Invoice has been successfully updated.",
        });
      } else {
        const newInvoice = await createInvoice(formData);
        toast({
          title: "Invoice Created",
          description: "Invoice has been successfully created.",
        });
        // Navigate to the new invoice
        navigate(`/invoices/${newInvoice.id}/edit`);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to save invoice",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // Send invoice handler
  const handleSend = async () => {
    if (!selectedInvoice && !isEditing) {
      toast({
        title: "Error",
        description: "Please save the invoice before sending",
        variant: "destructive",
      });
      return;
    }

    try {
      setSending(true);
      await sendInvoice(invoiceId);
      toast({
        title: "Invoice Sent",
        description: "Invoice has been successfully sent to the customer.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to send invoice",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  // Preview handler
  const handlePreview = () => {
    // TODO: Open preview modal or navigate to preview page
    toast({
      title: "Preview",
      description: "Invoice preview feature coming soon!",
    });
  };

  // Connection error
  if (!isConnected) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertDescription>
            Not connected to server. Please check your connection and try again.
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  // Loading state for initial load
  if ((isEditing && invoicesLoading && !selectedInvoice) || (customersLoading && customers.length === 0)) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading invoice...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={handleBack} className="p-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isEditing ? 'Edit Invoice' : 'Create Invoice'}
            </h1>
            <p className="text-muted-foreground">
              {isEditing
                ? `Invoice #${selectedInvoice?.invoice_number || invoiceId}`
                : 'Create a new invoice for your customer'
              }
            </p>
          </div>
        </div>

        {/* Action buttons for editing mode */}
        {isEditing && selectedInvoice && (
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handlePreview}>
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button variant="default" onClick={handleSend} disabled={sending}>
              <Send className="h-4 w-4 mr-2" />
              {sending ? 'Sending...' : 'Send Invoice'}
            </Button>
          </div>
        )}
      </div>

      {/* Error Alert */}
      {(invoicesError || customersError) && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            {invoicesError || customersError}
            <Button
              variant="outline"
              size="sm"
              onClick={clearInvoicesError}
              className="ml-2"
            >
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Main Form */}
      <div className="max-w-4xl mx-auto">
        <InvoiceFormShadcn
          invoice={selectedInvoice}
          customers={customers}
          onSubmit={handleSubmit}
          onCancel={handleBack}
          loading={saving}
          isEditing={isEditing}
        />
      </div>
    </PageContainer>
  );
};

export default InvoiceBuilderShadcn;